import React, { FC, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Prompt } from 'react-router';
import { Button } from '@clearcover/atlas';

import { Body } from 'QuoteApp/components';
import { useAppContext } from 'QuoteApp/services/providers';
import { useCheckoutLinkPolling } from 'QuoteApp/services/providers/AppProvider/hooks/useCheckoutLinkPolling';
import { useCheckoutLink } from 'QuoteApp/services/providers/AppProvider/hooks/useCheckoutLink';
import { Confetti } from 'QuoteApp/screens/CongratsAgent/Confetti';
import { PolicyManagement } from 'QuoteApp/screens/CongratsAgent/PolicyManagement';
import { PostBindSteps } from 'QuoteApp/screens/CongratsAgent/PostBindSteps';
import { useToast } from 'Shared/components';

import { StatusCard } from './StatusCard';
import styles from './styles.module.css';

export const CheckoutLinkCongrats: FC = () => {
  const history = useHistory();
  const { appId, details } = useAppContext();
  const { toast } = useToast();

  const isBound = details.status === 'bound';

  const { checkoutLink, fetchCheckoutLink } = useCheckoutLink(appId);
  const policyId = checkoutLink?.policyId;
  const policyNumber = checkoutLink?.policyNumber;

  const showPolicyManagement = !!policyId;

  useEffect(() => {
    if (isBound) {
      void fetchCheckoutLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCheckoutLink]);

  const { pollingTimedOut, resumePolling } = useCheckoutLinkPolling(
    !isBound,
    () => {
      void fetchCheckoutLink();
      toast({
        type: 'success',
        message: 'Customer has completed the order',
        withSecondaryNav: false,
        position: 'top',
        icon: 'check',
      });
    }
  );

  return (
    <Body data-testid="checkout-link-congrats-screen">
      <Prompt
        when={!isBound}
        message="Navigating away may affect the customer's checkout link. Are you sure you want to leave?"
      />

      {isBound && (
        <div className={styles.confetti}>
          <Confetti />
        </div>
      )}

      <div className={styles.page}>
        <div className={styles.content}>
          <StatusCard
            isBound={isBound}
            details={details}
            policyNumber={policyNumber}
            pollingTimedOut={pollingTimedOut}
            onResumePolling={resumePolling}
          />
          <div className={styles.stepsSection}>
            <div className={styles.stepsHeading}>
              Important! Here's how to finalize the policy.
            </div>
            <div className={styles.stepsSubHeading}>
              This is what your customer needs to do next. (We'll also email
              them instructions.)
            </div>
            <PostBindSteps />
          </div>
        </div>

        {showPolicyManagement && <PolicyManagement policyId={policyId!} />}

        <div className={styles.actions}>
          <Button
            variant="secondary"
            className={styles.viewPolicyButton}
            data-testid="view-policy-button"
            disabled={!isBound || !policyId}
            onClick={() => {
              if (isBound && policyId) {
                history.push(`/policies/${policyId}`);
              }
            }}
          >
            View Policy
          </Button>
          <Link to="/quotes">
            <Button
              variant="primary"
              data-testid="return-to-dashboard-button"
              className={styles.returnButton}
            >
              Return to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </Body>
  );
};
